import { useCallback } from 'react';
import { useAppStore } from '../stores/app';
import { useSocket, useSocketEvent } from '../lib/socket';
import { hapticFeedback } from '../lib/telegram';
import type { 
  RoomDTO, 
  PlayerDTO, 
  UserId,
  QuestionDisplay,
  QuestionForPlayer,
  RoundResults,
  GameFinishedData,
} from '@beshek/shared';

export function useGameEvents() {
  const socket = useSocket();
  const store = useAppStore();
  const userId = store.userId;

  // ============================================
  // Room Events
  // ============================================

  useSocketEvent('room:created', useCallback((data: { room: RoomDTO }) => {
    store.setRoom(data.room);
    store.setScreen('lobby');
  }, [store]));

  useSocketEvent('room:player_joined', useCallback((data: { player: PlayerDTO }) => {
    store.addPlayer(data.player);
    hapticFeedback('light');
  }, [store]));

  useSocketEvent('room:player_left', useCallback((data: { playerId: UserId }) => {
    store.removePlayer(data.playerId);
  }, [store]));

  useSocketEvent('room:reset', useCallback(() => {
    store.resetGame();
    store.setScreen('lobby');
  }, [store]));

  useSocketEvent('room:player_ready', useCallback((data: { playerId: UserId; isReady: boolean }) => {
    store.updatePlayerReady(data.playerId, data.isReady);
  }, [store]));
  useSocketEvent('room:player_updated', useCallback((data: { player: PlayerDTO }) => {
    store.updatePlayer(data.player);
  }, [store]));

  useSocketEvent('sync:state', useCallback((data: { room: RoomDTO }) => {
    store.setRoom(data.room);
    store.setScreen('lobby');
  }, [store]));

  // ============================================
  // Game Events
  // ============================================

  useSocketEvent('game:preparing', useCallback(() => {
    store.setGameStatus('preparing');
  }, [store]));

  useSocketEvent('game:started', useCallback((data: { gameId: string; totalQuestions: number; resultsDisplayTime?: number }) => {
    store.setGameStarted(data.gameId, data.totalQuestions, data.resultsDisplayTime);
    hapticFeedback('medium');
  }, [store]));

  useSocketEvent('game:countdown', useCallback((data: { seconds: number }) => {
    store.setCountdown(data.seconds);
  }, [store]));

  useSocketEvent('game:question', useCallback((data: QuestionDisplay | QuestionForPlayer) => {
    if ('question' in data) {
      store.setQuestion({
        questionId: data.question.id,
        questionText: data.question.text,
        options: data.question.options,
        round: data.round,
      });
    } else {
      store.setQuestion({
        questionId: data.questionId,
        questionText: (data as any).questionText,
        options: data.options,
        round: data.round,
      });
    }
    hapticFeedback('medium');
  }, [store]));

  useSocketEvent('game:answer_received', useCallback(() => {
    store.setAnswerSent();
    hapticFeedback('success');
  }, [store]));

  useSocketEvent('game:round_results', useCallback((data: RoundResults) => {
    if (userId) {
      store.setRoundResults(data, userId);
      const myResult = data.playerResults.find(r => r.playerId === userId);
      if (myResult?.isCorrect) {
        hapticFeedback('success');
      } else {
        hapticFeedback('error');
      }
    }
  }, [store, userId]));

  useSocketEvent('game:finished', useCallback((data: GameFinishedData) => {
    store.setGameFinished(data.leaderboard, data.stats);
    hapticFeedback('success');
  }, [store]));

  // ============================================
  // Actions
  // ============================================

  const createRoom = useCallback(() => {
    if (!socket) return;
    socket.emit('room:create', {}, (response) => {
      if ('error' in response) {
        store.setError(response.error);
      } else if ('room' in response) {
        store.setRoom(response.room);
        store.setScreen('lobby');
      }
    });
  }, [socket, store]);

  const joinRoom = useCallback((code: string) => {
    if (!socket) return;
    socket.emit('room:join', { code: code.toUpperCase() }, (response) => {
      if ('error' in response) {
        store.setError(response.error);
      } else if ('room' in response) {
        store.setRoom(response.room);
        store.setScreen('lobby');
      }
    });
  }, [socket, store]);

  const setReady = useCallback((isReady: boolean) => {
    if (!socket) return;
    socket.emit('room:ready', { isReady });
  }, [socket]);

  const startGame = useCallback((settings: { topic: string; totalQuestions?: number; timePerQuestion?: number; resultsDisplayTime?: number; difficulty?: string; gameMode?: 'quiz' | 'truefalse' }) => {
    if (!socket) return;
    socket.emit('game:start', {
      settings: {
        topic: settings.topic,
        totalQuestions: settings.totalQuestions || 10,
        timePerQuestion: settings.timePerQuestion || 20,
        resultsDisplayTime: settings.resultsDisplayTime || 5,
        difficulty: (settings.difficulty as 'easy' | 'medium' | 'hard') || 'medium',
        basePoints: 100,
        speedBonus: true,
        familyFriendly: true,
        gameMode: settings.gameMode || 'quiz',
      },
    }, (response) => {
      if ('error' in response) {
        store.setError(response.error);
      }
    });
  }, [socket, store]);

  const submitAnswer = useCallback((answerIndex: number) => {
    if (!socket) return;
    const questionId = store.questionId;
    if (!questionId) return;
    store.selectAnswer(answerIndex);
    socket.emit('game:answer', { questionId, answerIndex });
  }, [socket, store]);

  const resetRoom = useCallback(() => {
    // Если socket недоступен — полный reset на главную
    if (!socket || !socket.connected) {
      console.log('Socket disconnected, doing full reset');
      store.reset();
      return;
    }
    
    // Таймаут на случай если сервер не отвечает
    const timeout = setTimeout(() => {
      console.log('Reset timeout, doing full reset');
      store.reset();
    }, 3000);
    
    socket.emit('room:reset', (response: any) => {
      clearTimeout(timeout);
      if (response?.success) {
        store.resetGame();
        store.setScreen('lobby');
      } else {
        console.error('Reset failed:', response?.error);
        // Fallback — полный reset
        store.reset();
      }
    });
  }, [socket, store]);

  const leaveRoom = useCallback(() => {
    if (!socket) return;
    socket.emit('room:leave');
    store.reset();
    window.location.href = '/?mode=play';
  }, [socket, store]);

  return {
    createRoom,
    joinRoom,
    setReady,
    startGame,
    submitAnswer,
    leaveRoom,
    resetRoom,
  };
}